import Queue from "p-queue";
import { Upload } from "@/api/studio/uploads/types/upload.type";
import { FullPageLoader } from "@/components/fallbacks";
import { StudiesLayout } from "@/components/layouts";
import { useUploadsStore } from "@/components/ui/drag-drop-upload";
import { Progress } from "@/components/ui/progress";
import { useSegment } from "@/hooks/use-segment";
import { useEffect, useMemo, useState } from "react";
import { stageContextUpload, uploadUpload } from "@/api/studio/uploads/api";
import { addContextToPack } from "@/api/studio/contexts/api";
import { useQueryClient } from "@tanstack/react-query";
import { ContextPackDropzoneSkeleton } from "@/features/contexts/components/context-pack-dropzone-skeleton";
import { useStudyNavigation } from "@/features/studies/hooks/use-study-navigation";

/* Prop Types */
interface StudyAssetContextPackUploadRouteProps {
  projectId: string;
  studyId: string;
  contextPackId: string;
}

/*
 * StudyAssetContextPackUploadRoute component
 *
 * Stages files, uploads them to S3 and adds them to the contextPack.
 */
export const StudyAssetContextPackUploadRoute = ({ projectId, studyId, contextPackId }: StudyAssetContextPackUploadRouteProps) => {
  /* Stores */
  const uploads = useUploadsStore((state) => state.uploads);
  const { reset } = useUploadsStore((state) => state.uploadActions);
  const qc = useQueryClient();

  /* State */
  const [progress, setProgress] = useState(1);
  const [status, setStatus] = useState("waiting");

  /* Hooks */
  const { segmentTrackEvent } = useSegment();
  const { navigateTo } = useStudyNavigation(projectId, studyId, "study-context");

  /* Queue */
  const queue = useMemo(() => new Queue({ concurrency: 5 }), []);

  queue.on("active", () => {
    setStatus("active");
  });

  queue.on("empty", () => {
    setStatus("empty");
  });

  queue.on("error", () => {
    setStatus("error");
  });

  queue.on("next", () => {
    setProgress(progress + 1);
  });

  queue.on("idle", () => {
    // Remove all uploads from the store
    reset();
    navigateTo(`/projects/${projectId}/studies/${studyId}/asset/study-context/${contextPackId}/dropzone`);
  });

  const worker = async (upload: Upload) => {
    // Stage and upload the file in S3
    const contextId = await stageContextUpload(contextPackId).then(async ({ url, id }) => {
      // upload the file
      return await uploadUpload({ key: id, file: upload.file, url });
    });

    // Add the asset to the context pack
    await addContextToPack({ contextId, name: upload.name, packId: contextPackId })
      .then(() => {
        //invalidate contextpack query
        qc.invalidateQueries({ queryKey: ["contextPack", contextPackId] });
      })
      .then(() => {
        segmentTrackEvent("Context Pack Image Uploaded", {
          fileName: upload.name,
          fileSize: upload.file.size,
        });
      });
  };

  /* Side effects */
  useEffect(() => {
    //start the queue
    uploads.forEach((upload) => queue.add(() => worker(upload)));
  }, []);

  /* Render */
  return (
    <StudiesLayout projectId={projectId} studyId={studyId} currentStep="study-context">
      {(status === "active" || status === "empty") && (
        <FullPageLoader>
          <div className="flex flex-col justify-center gap-2 items-center">
            <video src="/videos/review-animation.webm" className="w-48 pb-2" autoPlay muted loop />
            <h2>Preparing context images</h2>
            <Progress value={(progress / uploads.length) * 100} staticColorClass="bg-success" className="w-1/2 mt-4" />
          </div>
        </FullPageLoader>
      )}
      <ContextPackDropzoneSkeleton />
    </StudiesLayout>
  );
};
